'use client'

import { useEffect, useState } from 'react'
import { XMarkIcon, ArrowTopRightOnSquareIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'

/**
 * 檢測是否在社交軟體的 WebView 中運行
 */
const isInWebView = (): boolean => {
  if (typeof window === 'undefined') return false
  
  const ua = navigator.userAgent.toLowerCase()
  
  // 檢測各種社交軟體的 WebView
  return (
    ua.includes('line/') ||           // LINE
    ua.includes('instagram') ||       // Instagram
    ua.includes('fban/') ||           // Facebook App (Android)
    ua.includes('fbav/') ||           // Facebook App (iOS)
    ua.includes('twitter') ||          // Twitter
    ua.includes('snapchat') ||         // Snapchat
    ua.includes('kakaotalk') ||        // KakaoTalk
    ua.includes('messenger') ||        // Facebook Messenger
    ua.includes('whatsapp') ||         // WhatsApp
    ua.includes('wechat') ||           // WeChat
    ua.includes('qqbrowser') ||        // QQ Browser
    ua.includes('ucbrowser') ||        // UC Browser
    // 檢測是否在 iOS 的 WebView 中（非 Safari）
    (ua.includes('iphone') || ua.includes('ipad')) && ua.includes('safari') === false && ua.includes('version/') === false
  )
}

/**
 * 獲取當前平台的名稱
 */
const getPlatformName = (): string => {
  if (typeof window === 'undefined') return '此應用'
  
  const ua = navigator.userAgent.toLowerCase()
  
  if (ua.includes('line/')) return 'LINE'
  if (ua.includes('instagram')) return 'Instagram'
  if (ua.includes('fban/') || ua.includes('fbav/')) return 'Facebook'
  if (ua.includes('twitter')) return 'Twitter'
  if (ua.includes('snapchat')) return 'Snapchat'
  if (ua.includes('kakaotalk')) return 'KakaoTalk'
  if (ua.includes('messenger')) return 'Messenger'
  if (ua.includes('whatsapp')) return 'WhatsApp'
  if (ua.includes('wechat')) return 'WeChat'
  
  return '此應用'
}

/**
 * 檢測平台類型
 */
const getPlatform = (): 'ios' | 'android' | 'other' => {
  if (typeof window === 'undefined') return 'other'
  
  const ua = navigator.userAgent.toLowerCase()
  
  if (ua.includes('iphone') || ua.includes('ipad')) return 'ios'
  if (ua.includes('android')) return 'android'
  
  return 'other'
}

/**
 * 複製連結到剪貼板
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    } else {
      // 降級方案：使用傳統方法
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textArea)
      return success
    }
  } catch (error) {
    // console.error('複製到剪貼板失敗:', error) // 已註釋
    return false
  }
}

/**
 * 安全白名單：只允許開啟自己網站的連結
 */
const isAllowedUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url)
    const allowedDomains = [
      'www.jlpt-aitutor.com',
      'jlpt-aitutor.com',
      'localhost',
      '127.0.0.1'
    ]
    return allowedDomains.some(domain => urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain))
  } catch {
    return false
  }
}

/**
 * 事件追蹤（可擴展為 Analytics）
 */
const trackEvent = (eventName: string, data?: Record<string, any>) => {
  // 可以整合 Google Analytics、Mixpanel 等
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, data)
  }
  // 本地記錄（開發用）- 已註釋
  // if (process.env.NODE_ENV === 'development') {
  //   console.log('[WebView] Event:', eventName, data)
  // }
}

/**
 * Android: 嘗試開啟 Chrome
 */
const tryOpenChrome = (url: string): boolean => {
  try {
    const urlObj = new URL(url)
    const hostPath = urlObj.host + urlObj.pathname + urlObj.search + urlObj.hash
    const chromeIntent = `intent://${hostPath}#Intent;scheme=https;package=com.android.chrome;action=android.intent.action.VIEW;end`
    
    // 方法 1: 使用 iframe（某些 WebView 需要）
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.src = chromeIntent
    document.body.appendChild(iframe)
    
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe)
      }
    }, 1000)
    
    // 方法 2: 直接設置 location（主要方法）
    window.location.href = chromeIntent
    
    trackEvent('android_chrome_intent_attempted', { url })
    return true
  } catch (error) {
    trackEvent('android_chrome_intent_failed', { error: String(error) })
    return false
  }
}

/**
 * Android: 嘗試開啟 Samsung Internet
 */
const tryOpenSamsungInternet = (url: string): boolean => {
  try {
    const urlObj = new URL(url)
    const hostPath = urlObj.host + urlObj.pathname + urlObj.search + urlObj.hash
    const samsungIntent = `intent://${hostPath}#Intent;scheme=https;package=com.sec.android.app.sbrowser;action=android.intent.action.VIEW;end`
    
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.src = samsungIntent
    document.body.appendChild(iframe)
    
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe)
      }
    }, 1000)
    
    window.location.href = samsungIntent
    
    trackEvent('android_samsung_intent_attempted', { url })
    return true
  } catch (error) {
    trackEvent('android_samsung_intent_failed', { error: String(error) })
    return false
  }
}

/**
 * iOS: 嘗試所有可行的外開策略（用戶點擊觸發）
 * 注意：iOS 不允許自動跳轉，必須由用戶手勢觸發
 */
const tryOpenIOSBrowser = (url: string): boolean => {
  // 策略 1: 嘗試 window.open（用戶點擊時可能有效）
  try {
    const opened = window.open(url, '_blank', 'noopener,noreferrer')
    if (opened && !opened.closed) {
      trackEvent('ios_window_open_blank_attempted', { url })
      return true
    }
  } catch (error) {
    trackEvent('ios_window_open_blank_failed', { error: String(error) })
  }
  
  // 策略 2: 嘗試 window.open with _system（某些 WebView 可能支援）
  try {
    const opened = (window as any).open(url, '_system')
    if (opened) {
      trackEvent('ios_window_open_system_attempted', { url })
      return true
    }
  } catch (error) {
    trackEvent('ios_window_open_system_failed', { error: String(error) })
  }
  
  // 策略 3: 嘗試直接設置 location（某些 WebView 可能允許）
  try {
    window.location.href = url
    trackEvent('ios_location_href_attempted', { url })
    return true
  } catch (error) {
    trackEvent('ios_location_href_failed', { error: String(error) })
  }
  
  return false
}

/**
 * 在外部瀏覽器開啟連結（改進版）
 */
const openInBrowser = async (url: string): Promise<{ success: boolean; method: string; needsFallback: boolean }> => {
  const platform = getPlatform()
  const currentUrl = url || window.location.href
  
  // 安全檢查：只允許開啟白名單內的網址
  if (!isAllowedUrl(currentUrl)) {
    trackEvent('url_blocked_by_whitelist', { url: currentUrl })
    return { success: false, method: 'blocked', needsFallback: false }
  }
  
  // 步驟 1: 先複製連結到剪貼板（作為備援）
  await copyToClipboard(currentUrl)
  trackEvent('url_copied_to_clipboard', { url: currentUrl })
  
  // 步驟 2: 根據平台嘗試開啟瀏覽器
  if (platform === 'android') {
    // Android: 優先用 Chrome，失敗再試 Samsung Internet
    if (tryOpenChrome(currentUrl)) {
      return { success: true, method: 'android_chrome', needsFallback: false }
    }
    
    // 降級：嘗試 Samsung Internet
    if (tryOpenSamsungInternet(currentUrl)) {
      return { success: true, method: 'android_samsung', needsFallback: false }
    }
    
    // 最後降級：返回需要顯示引導式浮條
    trackEvent('android_all_methods_failed', { url: currentUrl })
    return { success: false, method: 'android_failed', needsFallback: true }
    
  } else if (platform === 'ios') {
    // iOS: 嘗試所有策略
    if (tryOpenIOSBrowser(currentUrl)) {
      return { success: true, method: 'ios_auto', needsFallback: false }
    }
    
    // 所有自動方法失敗，返回需要顯示引導式浮條
    trackEvent('ios_all_methods_failed', { url: currentUrl })
    return { success: false, method: 'ios_failed', needsFallback: true }
  }
  
  // 其他平台：嘗試通用方法
  try {
    const opened = window.open(currentUrl, '_blank', 'noopener,noreferrer')
    if (opened && !opened.closed) {
      trackEvent('other_platform_window_open', { url: currentUrl })
      return { success: true, method: 'window_open', needsFallback: false }
    }
  } catch (error) {
    trackEvent('other_platform_failed', { error: String(error) })
  }
  
  return { success: false, method: 'unknown_failed', needsFallback: true }
}

interface WebViewDetectorProps {
  /** 是否只在首頁顯示 */
  onlyOnHomepage?: boolean
  /** 自訂關閉後的 localStorage key */
  dismissKey?: string
}

export default function WebViewDetector({ 
  onlyOnHomepage = false,
  dismissKey = 'webview_detector_dismissed'
}: WebViewDetectorProps) {
  const [isWebView, setIsWebView] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const [platformName, setPlatformName] = useState('此應用')
  const [isOpening, setIsOpening] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [showDebug, setShowDebug] = useState(false)
  const [forceOpen, setForceOpen] = useState(false)
  const [showFallback, setShowFallback] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const debugMode = urlParams.get('debug') === 'webview'
      const forceOpenParam = urlParams.get('force_open_browser') === '1'
      
      setShowDebug(debugMode)
      setForceOpen(forceOpenParam)

      // 檢查是否在 WebView 中
      const detected = isInWebView()
      setIsWebView(detected)
      
      if (detected) {
        setPlatformName(getPlatformName())
        
        // 每次開啟都顯示提示（不檢查 localStorage，不保存關閉狀態）
        // 清除可能存在的關閉狀態，確保每次開啟都顯示提示
        setIsDismissed(false)

        // 不再自動嘗試開啟瀏覽器，改為用戶點擊觸發
        // 這樣可以提高 iOS 的成功率（iOS 需要用戶手勢觸發）
        trackEvent('webview_detected', { 
          platform: getPlatform(),
          platformName: getPlatformName()
        })
      }
    }
  }, [dismissKey])

  // 調試面板（即使組件不顯示，調試模式也要顯示）
  const debugPanel = showDebug && typeof window !== 'undefined' ? (
    <div className="fixed top-4 left-4 right-4 z-[100] bg-yellow-900 border-2 border-yellow-500 rounded-lg p-4 text-white text-xs max-h-[60vh] overflow-y-auto">
      <div className="font-bold text-yellow-300 mb-2">🐛 WebView 調試信息</div>
      <div className="space-y-1">
        <div><strong>User Agent:</strong> {navigator.userAgent}</div>
        <div><strong>檢測到 WebView:</strong> {isWebView ? '✅ 是' : '❌ 否'}</div>
        <div><strong>平台名稱:</strong> {platformName}</div>
        <div><strong>已關閉提示（當前頁面）:</strong> {isDismissed ? '✅ 是' : '❌ 否'}</div>
        <div><strong>localStorage 狀態:</strong> {localStorage.getItem(dismissKey) || '未設置'} <span className="text-yellow-400 text-xs">(已不使用，每次開啟都會顯示提示)</span></div>
        <div><strong>當前路徑:</strong> {window.location.pathname}</div>
        <div><strong>onlyOnHomepage:</strong> {onlyOnHomepage ? '是' : '否'}</div>
        <div><strong>組件應顯示:</strong> {
          (isWebView && !isDismissed && (!onlyOnHomepage || window.location.pathname === '/')) 
            ? '✅ 是' 
            : '❌ 否'
        }</div>
        <div className="mt-2 pt-2 border-t border-yellow-700">
          <strong>檢測詳情:</strong>
          <div className="ml-2 mt-1">
            <div>LINE: {navigator.userAgent.toLowerCase().includes('line/') ? '✅' : '❌'}</div>
            <div>LINE (其他): {/line/i.test(navigator.userAgent) ? '✅' : '❌'}</div>
            <div>完整 UA: {navigator.userAgent}</div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-yellow-700 space-y-2">
          <div className="font-bold text-yellow-300 mb-1">🔧 操作按鈕</div>
          <button
            onClick={() => {
              localStorage.removeItem(dismissKey)
              window.location.reload()
            }}
            className="w-full px-3 py-2 bg-yellow-600 hover:bg-yellow-700 rounded text-white text-sm font-medium"
          >
            🔄 清除關閉狀態並重新載入
          </button>
          <button
            onClick={async () => {
              const result = await openInBrowser(window.location.href)
              if (result.success) {
                toast.success('正在開啟瀏覽器...', {
                  description: '連結已複製到剪貼板',
                  duration: 3000
                })
              } else {
                toast.error('無法自動開啟', {
                  description: '連結已複製，請手動貼上到瀏覽器',
                  duration: 4000
                })
              }
            }}
            className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-white text-sm font-medium"
          >
            🌐 強制開啟瀏覽器
          </button>
          <button
            onClick={async () => {
              const success = await copyToClipboard(window.location.href)
              if (success) {
                toast.success('連結已複製', { duration: 2000 })
              } else {
                toast.error('複製失敗', { duration: 2000 })
              }
            }}
            className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm font-medium"
          >
            📋 複製連結
          </button>
        </div>
      </div>
    </div>
  ) : null

  // 如果不在 WebView 中，或已關閉提示，只顯示調試面板（如果啟用）
  if (!isWebView || isDismissed) {
    return debugPanel
  }

  // 如果設定為只在首頁顯示，檢查當前路徑
  if (onlyOnHomepage && typeof window !== 'undefined' && window.location.pathname !== '/') {
    return debugPanel
  }

  const handleDismiss = () => {
    // 只關閉當前頁面的提示，不保存到 localStorage
    // 下次開啟時會重新顯示提示
    setIsDismissed(true)
    // localStorage.setItem(dismissKey, 'true') // 已移除：每次開啟都顯示提示
  }

  const handleOpenInBrowser = async (e?: React.MouseEvent) => {
    // 確保事件正確觸發（iOS 需要）
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    
    setIsOpening(true)
    
    try {
      const result = await openInBrowser(window.location.href)
      const platform = getPlatform()
      
      if (result.success) {
        toast.success('正在開啟瀏覽器...', {
          description: '連結已複製到剪貼板，如果瀏覽器未自動開啟，請手動貼上連結',
          duration: 3000
        })
        setIsCopied(true)
        
        // 2 秒後自動關閉提示（給用戶時間看到回饋）
        setTimeout(() => {
          handleDismiss()
        }, 2000)
      } else if (result.needsFallback || (platform === 'ios' && !result.success)) {
        // iOS 或需要顯示引導式浮條
        setShowFallback(true)
        setIsCopied(true)
        // 確保連結已複製
        await copyToClipboard(window.location.href)
      } else {
        toast.error('無法自動開啟瀏覽器', {
          description: '連結已複製到剪貼板，請手動貼上到瀏覽器開啟',
          duration: 4000
        })
        setIsCopied(true)
      }
    } catch (error) {
      // console.error('開啟瀏覽器時發生錯誤:', error) // 已註釋
      const platform = getPlatform()
      if (platform === 'ios') {
        // iOS 發生錯誤時，顯示 fallback
        setShowFallback(true)
        setIsCopied(true)
        await copyToClipboard(window.location.href)
      } else {
        toast.error('發生錯誤', {
          description: '請手動複製連結到瀏覽器開啟',
          duration: 4000
        })
      }
    } finally {
      setIsOpening(false)
    }
  }

  const handleCopyLink = async () => {
    const success = await copyToClipboard(window.location.href)
    if (success) {
      setIsCopied(true)
      toast.success('連結已複製', {
        description: '請貼上到瀏覽器開啟',
        duration: 2000
      })
    } else {
      toast.error('複製失敗', {
        description: '請手動複製網址列中的連結',
        duration: 3000
      })
    }
  }

  // iOS 引導式浮條（當自動開啟失敗時顯示）
  const fallbackBanner = showFallback ? (
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-white dark:bg-gray-800 shadow-2xl border-t border-gray-200 dark:border-gray-700 p-4 safe-area-inset-bottom">
      <div className="max-w-md mx-auto">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
            <ArrowTopRightOnSquareIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              在瀏覽器中開啟
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              連結已複製到剪貼板
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <p><strong>方法一：</strong>點擊下方按鈕嘗試開啟</p>
              <p><strong>方法二：</strong>點擊右上角「⋯」或「分享」按鈕 → 選擇「在 Safari 開啟」</p>
              <p><strong>方法三：</strong>手動複製連結到 Safari 瀏覽器</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async (e) => {
              e.preventDefault()
              e.stopPropagation()
              const result = await openInBrowser(window.location.href)
              if (!result.success) {
                // 再次嘗試失敗，顯示更明確的提示
                toast.info('請使用右上角的「⋯」或「分享」按鈕，選擇「在 Safari 開啟」', {
                  duration: 6000
                })
              }
            }}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <ArrowTopRightOnSquareIcon className="w-5 h-5" />
            嘗試開啟 Safari
          </button>
          <button
            onClick={() => setShowFallback(false)}
            className="px-4 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            aria-label="關閉"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  ) : null

  return (
    <>
      {debugPanel}
      {fallbackBanner}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* 關閉按鈕 */}
        {!isOpening && (
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            aria-label="關閉提示"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}

        {/* 標題 */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-white mb-2">
            建議使用瀏覽器開啟
          </h3>
          <p className="text-sm text-gray-300">
            您目前正在 {platformName} 的內建瀏覽器中瀏覽
          </p>
        </div>

        {/* 說明內容 */}
        <div className="mb-6 space-y-2 text-sm text-gray-400">
          <p>
            為了獲得最佳體驗，建議您使用外部瀏覽器（如 Chrome、Safari）開啟此網站。
          </p>
          <p>
            內建瀏覽器可能會限制部分功能，例如：
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>登入功能可能無法正常運作</li>
            <li>語音播放可能受限</li>
            <li>部分互動功能可能異常</li>
          </ul>
        </div>

        {/* 操作按鈕 */}
        <div className="flex flex-col gap-3">
          <button
            onClick={(e) => handleOpenInBrowser(e)}
            disabled={isOpening}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isOpening ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                正在開啟瀏覽器...
              </>
            ) : (
              <>
                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                在瀏覽器開啟
              </>
            )}
          </button>
          
          <button
            onClick={handleCopyLink}
            disabled={isOpening || isCopied}
            className="w-full bg-gray-800 hover:bg-gray-700 disabled:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed text-gray-300 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isCopied ? (
              <>
                <CheckIcon className="w-5 h-5 text-green-400" />
                連結已複製
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="w-5 h-5" />
                複製連結
              </>
            )}
          </button>
          
          {!isOpening && (
            <button
              onClick={handleDismiss}
              className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              稍後提醒
            </button>
          )}
        </div>

        {/* 提示：如何複製連結 */}
        {!isCopied && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <p className="text-xs text-gray-500 text-center">
              提示：如果自動開啟失敗，連結已自動複製，請手動貼上到瀏覽器
            </p>
          </div>
        )}
      </div>
    </div>
    </>
  )
}

